import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { Plus, Upload, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { DiscussButton } from "@/components/DiscussButton";

const EMPTY = { po_id: "", po_number: "", supplier_id: "", supplier_name: "", client_id: "", client_name: "", amount: 0, vat_amount: 0, vat_status: "vatable", invoice_number: "", invoice_date: "", due_date: "", description: "", attachments: [] };

const VAT_STATUS_OPTIONS = [
  { value: "vatable", label: "Vatable (12%)" },
  { value: "zero_rated", label: "Zero-Rated (0%)" },
  { value: "exempt", label: "VAT-Exempt" },
];

export default function BillingRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  // Auto-compute Input VAT at 12% of the entered gross Amount whenever
  // VAT Status is Vatable. Manual override supported.
  const [vatAutoMode, setVatAutoMode] = useState(true);
  useEffect(() => {
    if (!vatAutoMode || form.vat_status !== "vatable") return;
    const gross = Number(form.amount) || 0;
    // VAT-inclusive: gross = net + 12% of net → VAT = gross × 12/112.
    const auto = Math.round((gross * 12 / 112) * 100) / 100;
    setForm((prev) => (Number(prev.vat_amount) === auto ? prev : { ...prev, vat_amount: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, form.vat_status, vatAutoMode]);

  // Payment dialog state — mirrors Receivables EWT flow. Recording marks
  // the billing request PAID and persists the EWT snapshot on the record.
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payEwtRate, setPayEwtRate] = useState("0");
  const [payMethod, setPayMethod] = useState("");
  const [payNote, setPayNote] = useState("");

  async function load() {
    const [b, p, c, s] = await Promise.all([
      api.get("/billing-requests"),
      api.get("/purchase-orders"),
      api.get("/clients"),
      api.get("/suppliers"),
    ]);
    setItems(b.data); setPos(p.data); setClients(c.data); setSuppliers(s.data);
  }
  useEffect(() => { load(); }, []);

  const canProcess = can(user, ["finance"]);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/billing-requests", { ...form, amount: Number(form.amount) });
      toast.success("Billing request submitted");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  }

  async function markPaid(bid) {
    const b = items.find((x) => x.id === bid);
    if (!b) return;
    setPayTarget(b);
    setPayEwtRate("0");
    setPayMethod("");
    setPayNote("");
    setPayOpen(true);
  }
  async function submitPayment(e) {
    e.preventDefault();
    if (!payTarget) return;
    const gross = Number(payTarget.amount) || 0;
    const vat = Number(payTarget.vat_amount) || 0;
    const vatable = Math.max(0, Math.round((gross - vat) * 100) / 100);
    const rate = Number(payEwtRate) || 0;
    const ewt = Math.round(vatable * (rate / 100) * 100) / 100;
    try {
      await api.post(`/billing-requests/${payTarget.id}/status`, {
        payment_status: "paid",
        ewt_rate: rate,
        ewt_amount: ewt,
        payment_method: payMethod || undefined,
        payment_note: payNote || undefined,
      });
      toast.success("Payment remitted");
      setPayOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }
  async function markProcessing(bid) {
    try {
      await api.post(`/billing-requests/${bid}/status`, { payment_status: "processing", status: "in_progress" });
      toast.success("Payment processing");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }

  async function uploadFiles(files) {
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE}/uploads/billing`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("upload failed");
        const j = await res.json();
        uploaded.push(j);
      }
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  }

  return (
    <div className="space-y-6" data-testid="billing-requests-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Module 04</div>
          <h1 className="section-title text-3xl sm:text-4xl">Billing Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">Request billing after delivery, attach docs, track payment status.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setVatAutoMode(true); setOpen(true); }} data-testid="new-billing-btn">
          <Plus size={16} className="mr-2" /> New Billing
        </Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1100px]">
          <TableHeader>
            <TableRow>
              <TableHead>Billing No.</TableHead>
              <TableHead>PO Number</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No billing requests yet.</TableCell></TableRow>
            ) : items.map((b) => (
              <TableRow key={b.id} data-testid={`billing-row-${b.id}`}>
                <TableCell className="font-mono text-xs">{b.billing_no}</TableCell>
                <TableCell className="font-mono text-xs">{b.po_number || "—"}</TableCell>
                <TableCell className="font-medium">{b.supplier_name}</TableCell>
                <TableCell className="text-sm">{b.invoice_number || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">₱{(b.amount || 0).toLocaleString()}</TableCell>
                <TableCell className="text-sm">{b.due_date || "—"}</TableCell>
                <TableCell><StatusBadge status={b.payment_status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {canProcess && b.payment_status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => markProcessing(b.id)} className="border-border" data-testid={`process-billing-${b.id}`}>Process</Button>
                    )}
                    {canProcess && b.payment_status !== "paid" && (
                      <Button size="sm" variant="outline" onClick={() => markPaid(b.id)} className="border-border" data-testid={`pay-billing-${b.id}`}>Remit Payment</Button>
                    )}
                    <ExportMenu endpointPath={`/exports/billing-request/${b.id}`} baseName={`billing_${b.billing_no}`} testId={`export-billing-${b.id}`} />
                    <DiscussButton recordType="billing_request" recordId={b.id} label={b.billing_no} testId={`discuss-billing-${b.id}`} />
                    {(user?.role === "admin" || b.submitted_by === user?.id) && !b.deleted_at && (
                      <ArchiveButton endpoint={`/billing-requests/${b.id}`} label={b.billing_no} onArchived={load} testId={`archive-billing-${b.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">New Billing Request</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Linked PO</Label>
                <Select value={form.po_id} onValueChange={(v) => {
                  const p = pos.find((x) => x.id === v);
                  // Try to also resolve the supplier record by name so TIN can be linked automatically.
                  const s = p ? suppliers.find((x) => (x.name || "").trim().toLowerCase() === (p.supplier_name || "").trim().toLowerCase()) : null;
                  setForm({
                    ...form,
                    po_id: v,
                    po_number: p?.po_number || "",
                    supplier_id: s?.id || form.supplier_id,
                    supplier_name: p?.supplier_name || form.supplier_name,
                    amount: p?.total_amount || form.amount,
                  });
                }}>
                  <SelectTrigger data-testid="billing-po-select"><SelectValue placeholder="Link to Purchase Order" /></SelectTrigger>
                  <SelectContent>
                    {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} — {p.supplier_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Supplier</Label>
                <Select
                  value={form.supplier_id}
                  onValueChange={(v) => {
                    const s = suppliers.find((x) => x.id === v);
                    setForm({ ...form, supplier_id: v, supplier_name: s?.name || "" });
                  }}
                >
                  <SelectTrigger data-testid="billing-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.tax_id ? `  ·  TIN ${s.tax_id}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Pick from the Suppliers list so the TIN links to the SLSP Purchases report. <a href="/suppliers" className="underline">Add a new supplier</a> if it's missing.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Linked Client (optional)</Label>
              <Select value={form.client_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_id: v, client_name: cl?.name || "" }); }}>
                <SelectTrigger data-testid="billing-client-select"><SelectValue placeholder="Select client this bill is for" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice No.</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="border-border" data-testid="billing-invoice-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Invoice Date</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="border-border" data-testid="billing-invdate-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border-border" data-testid="billing-due-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount (gross)</Label>
              <Input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border-border tabular-nums" data-testid="billing-amount-input" />
            </div>
            <div className="p-3 border border-border rounded bg-muted/30 grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">VAT Status</Label>
                <Select
                  value={form.vat_status}
                  onValueChange={(v) => setForm({ ...form, vat_status: v, vat_amount: v === "vatable" ? form.vat_amount : 0 })}
                >
                  <SelectTrigger data-testid="billing-vat-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">For BIR SLSP Purchases. Non-vatable purchases still get filed with 0 input VAT.</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Input VAT (₱)</Label>
                  {form.vat_status === "vatable" && (
                    vatAutoMode ? (
                      <span className="text-[9px] uppercase tracking-widest text-emerald-700" data-testid="billing-vat-auto-badge">Auto · 12%</span>
                    ) : (
                      <button type="button" className="text-[9px] uppercase tracking-widest text-muted-foreground underline decoration-dotted hover:text-foreground" onClick={() => setVatAutoMode(true)} data-testid="billing-vat-reset-auto-btn">Reset to 12%</button>
                    )
                  )}
                </div>
                <Input
                  type="number" min="0" step="0.01"
                  disabled={form.vat_status !== "vatable"}
                  value={form.vat_status === "vatable" ? form.vat_amount : 0}
                  onChange={(e) => { setVatAutoMode(false); setForm({ ...form, vat_amount: e.target.value }); }}
                  placeholder="0.00"
                  className="border-border tabular-nums font-heading disabled:opacity-60"
                  data-testid="billing-vat-input"
                />
                <p className="text-[10px] text-muted-foreground">{form.vat_status === "vatable" ? (vatAutoMode ? "Auto-computed from Amount × 12 ÷ 112 (VAT-inclusive). Type to override." : "Manual override — click “Reset to 12%” to restore auto.") : "Locked at 0 for non-vatable purchases."}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-border" data-testid="billing-desc-input" />
            </div>

            {/* Attachments */}
            <div className="space-y-2 border border-dashed border-border rounded-md p-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Paperclip size={14} /> Supporting documents
              </div>
              <div className="flex flex-wrap gap-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="border border-border rounded px-2 py-1 text-xs flex items-center gap-1.5 bg-muted/40">
                    <Paperclip size={12} /> {a.file_name}
                    <button type="button" onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-foreground">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer border border-border rounded px-3 py-1.5 hover:bg-muted" data-testid="billing-upload-btn">
                <Upload size={14} /> {uploading ? "Uploading…" : "Attach files"}
                <input type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(Array.from(e.target.files))} />
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="billing-submit-btn">Submit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Remittance dialog — same EWT waterfall used by Receivables so the */}
      {/* payables side captures BIR 2307 details when paying suppliers. */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="billing-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">Remit Payment — {payTarget?.billing_no}</DialogTitle></DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            {(() => {
              const gross = Number(payTarget?.amount) || 0;
              const vat = Number(payTarget?.vat_amount) || 0;
              const vatable = Math.max(0, Math.round((gross - vat) * 100) / 100);
              const rate = Number(payEwtRate) || 0;
              const ewt = Math.round(vatable * (rate / 100) * 100) / 100;
              const net = Math.max(0, Math.round((gross - ewt) * 100) / 100);
              return (
                <>
                  <div className="p-3 border border-border rounded bg-muted/30 text-sm space-y-1.5">
                    <div className="flex justify-between"><span className="text-muted-foreground">Supplier</span><span className="font-medium">{payTarget?.supplier_name}</span></div>
                    <div className="flex justify-between border-t border-border pt-1.5">
                      <span className="text-muted-foreground">Invoice Amount</span>
                      <span className="tabular-nums font-heading font-bold" data-testid="brpay-invoice-amt">₱{gross.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vatable Amount</span>
                      <span className="tabular-nums" data-testid="brpay-vatable-amt">₱{vatable.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Input VAT ({gross > 0 ? ((vat / gross) * 100).toFixed(2) : "0.00"}%)</span>
                      <span className="tabular-nums text-amber-700" data-testid="brpay-vat-amt">₱{vat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Less: EWT Withheld from Supplier</Label>
                    <Select value={payEwtRate} onValueChange={setPayEwtRate}>
                      <SelectTrigger data-testid="brpay-ewt-select" className="border-border"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">1% of Vatable Amount</SelectItem>
                        <SelectItem value="2">2% of Vatable Amount</SelectItem>
                        <SelectItem value="5">5% of Vatable Amount</SelectItem>
                        <SelectItem value="7">7% of Vatable Amount</SelectItem>
                        <SelectItem value="10">10% of Vatable Amount</SelectItem>
                      </SelectContent>
                    </Select>
                    {rate > 0 && (
                      <div className="flex justify-between text-xs pt-1">
                        <span className="text-muted-foreground">EWT ({rate}%) of Vatable</span>
                        <span className="tabular-nums text-rose-700" data-testid="brpay-ewt-amt">− ₱{ewt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border-2 border-emerald-200 rounded bg-emerald-50/60 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-800">Net Remittance</div>
                      <div className="text-[10px] text-emerald-700/80">Invoice Amount − EWT · Cash to remit</div>
                    </div>
                    <div className="tabular-nums font-heading font-black text-xl text-emerald-800" data-testid="brpay-net">
                      ₱{net.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Method</Label>
                      <Input value={payMethod} onChange={(e) => setPayMethod(e.target.value)} placeholder="Bank transfer, Cash, Check…" className="border-border" data-testid="brpay-method" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
                      <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Optional reference…" className="border-border" data-testid="brpay-note" />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="brpay-submit">Record remittance</Button>
                  </DialogFooter>
                </>
              );
            })()}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
